import { useState, useCallback } from "react";

import { apiBaseUrls } from "@platform/constants/shared-constants";
import API from "src/platform/http/shared-http";
import { ErrorSource, ErrorSeverity } from "src/platform/utils/errorHandler";

import { useErrorHandler } from "./useErrorHandler";

interface ApiOptions {
  /**
   * Whether to show loading state during API call
   * Default: true
   */
  showLoading?: boolean;

  /**
   * Whether to show success notifications
   * Default: false
   */
  showSuccessNotification?: boolean;

  /**
   * Whether to show error notifications
   * Default: true
   */
  showErrorNotification?: boolean;

  /**
   * Success message to display if the API call succeeds
   * Only used if showSuccessNotification is true
   */
  successMessage?: string;

  /**
   * Custom error message to display if the API call fails
   * If not provided, the error message from the API will be used
   */
  errorMessage?: string;

  /**
   * Additional metadata to include with error
   */
  metadata?: Record<string, unknown>;
}

/**
 * Hook for making API calls with standardized error handling and loading state
 */
export const useApi = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const { handleError, createError } = useErrorHandler();

  /**
   * Wrapper for API GET requests
   * @param endpoint API endpoint
   * @param options API call options
   * @returns Promise with API response
   */
  const get = useCallback(
    async <T>(
      endpoint: string,
      options: ApiOptions = {},
    ): Promise<T | null> => {
      const {
        showLoading = true,
        showErrorNotification = true,
        showSuccessNotification = false,
        successMessage,
        errorMessage,
        metadata,
      } = options;

      if (showLoading) setLoading(true);

      try {
        const api = new API(apiBaseUrls.BACKEND, endpoint);
        const response = await api.get<T>();

        if (showSuccessNotification && successMessage) {
          createError(
            successMessage,
            ErrorSource.API,
            ErrorSeverity.INFO,
            null,
            { showNotification: true, logError: false },
          );
        }

        return response;
      } catch (error) {
        handleError(error, {
          showNotification: showErrorNotification,
          customMessage: errorMessage,
          metadata: {
            endpoint,
            ...metadata,
          },
        });
        return null;
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [handleError, createError],
  );

  /**
   * Wrapper for API POST requests
   * @param endpoint API endpoint
   * @param data Request payload
   * @param options API call options
   * @returns Promise with API response
   */
  const post = useCallback(
    async <T, D = unknown>(
      endpoint: string,
      data: D,
      options: ApiOptions = {},
    ): Promise<T | null> => {
      const {
        showLoading = true,
        showErrorNotification = true,
        showSuccessNotification = false,
        successMessage,
        errorMessage,
        metadata,
      } = options;

      if (showLoading) setLoading(true);

      try {
        const api = new API(apiBaseUrls.BACKEND, endpoint);
        const response = await api.post<T, D>(data);

        if (showSuccessNotification && successMessage) {
          createError(
            successMessage,
            ErrorSource.API,
            ErrorSeverity.INFO,
            null,
            { showNotification: true, logError: false },
          );
        }

        return response;
      } catch (error) {
        handleError(error, {
          showNotification: showErrorNotification,
          customMessage: errorMessage,
          metadata: {
            endpoint,
            data,
            ...metadata,
          },
        });
        return null;
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [handleError, createError],
  );

  /**
   * Wrapper for API PATCH requests
   * @param endpoint API endpoint
   * @param data Request payload
   * @param options API call options
   * @returns Promise with API response
   */
  const patch = useCallback(
    async <T, D = unknown>(
      endpoint: string,
      data: D,
      options: ApiOptions = {},
    ): Promise<T | null> => {
      const {
        showLoading = true,
        showErrorNotification = true,
        showSuccessNotification = false,
        successMessage,
        errorMessage,
        metadata,
      } = options;

      if (showLoading) setLoading(true);

      try {
        const api = new API(apiBaseUrls.BACKEND, endpoint);
        const response = await api.patch<T, D>(data);

        if (showSuccessNotification && successMessage) {
          createError(
            successMessage,
            ErrorSource.API,
            ErrorSeverity.INFO,
            null,
            { showNotification: true, logError: false },
          );
        }

        return response;
      } catch (error) {
        handleError(error, {
          showNotification: showErrorNotification,
          customMessage: errorMessage,
          metadata: {
            endpoint,
            data,
            ...metadata,
          },
        });
        return null;
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [handleError, createError],
  );

  /**
   * Wrapper for API DELETE requests
   * @param endpoint API endpoint
   * @param options API call options
   * @returns Promise with API response
   */
  const del = useCallback(
    async <T>(
      endpoint: string,
      options: ApiOptions = {},
    ): Promise<T | null> => {
      const {
        showLoading = true,
        showErrorNotification = true,
        showSuccessNotification = false,
        successMessage,
        errorMessage,
        metadata,
      } = options;

      if (showLoading) setLoading(true);

      try {
        const api = new API(apiBaseUrls.BACKEND, endpoint);
        const response = await api.delete<T>();

        if (showSuccessNotification && successMessage) {
          createError(
            successMessage,
            ErrorSource.API,
            ErrorSeverity.INFO,
            null,
            { showNotification: true, logError: false },
          );
        }

        return response;
      } catch (error) {
        handleError(error, {
          showNotification: showErrorNotification,
          customMessage: errorMessage,
          metadata: {
            endpoint,
            ...metadata,
          },
        });
        return null;
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [handleError, createError],
  );

  return {
    loading,
    get,
    post,
    patch,
    delete: del,
  };
};
